"use client";

import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useEffect } from "react";

const safariStyles = `
  .gradient-text-white {
    background: linear-gradient(90deg, #ffffff, #ffffff, rgba(255, 255, 255, 0.8));
    background: -webkit-linear-gradient(90deg, #ffffff, #ffffff, rgba(255, 255, 255, 0.8));
    background: -moz-linear-gradient(90deg, #ffffff, #ffffff, rgba(255, 255, 255, 0.8));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }
  
  @supports not (-webkit-background-clip: text) {
    .gradient-text-white {
      background: none;
      color: #ffffff;
    }
  }
`;

const services = [
  "Brand Activation",
  "Integrated Marketing",
  "Website Development",
  "Video Production",
  "Brand Identity",
  "Brand Strategy"
];

export default function WhatWeDo() {
  useEffect(() => {
    // Inject Safari gradient styles
    const styleElement = document.createElement('style');
    styleElement.textContent = safariStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <section
      id="what-we-do"
      className="relative min-h-screen py-24 md:py-36 overflow-hidden"
    >
      {/* Subtle overlay for content readability */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        {/* Light overlay to ensure text readability without blocking the global gradient */}
        <div className="absolute inset-0 section-overlay" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6 gradient-text-white">
            What We Do
          </h2>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Elevate your brand and make your mark in history.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={service}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                ease: "easeOut" 
              }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              className="group relative"
            >
              {/* Service Card */}
              <div className="relative p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm hover:border-accent/30 transition-all duration-500 cursor-pointer">
                {/* Glassmorphism effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.08] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Content */}
                <div className="relative flex items-center justify-between">
                  <h3 className="text-2xl md:text-3xl font-semibold text-white group-hover:text-accent transition-colors duration-300">
                    {service}
                  </h3>
                  
                  {/* Animated Arrow */}
                  <motion.div
                    className="flex items-center justify-center w-12 h-12 rounded-full border border-white/20 group-hover:border-accent/50 transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      animate={{
                        x: [0, 4, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <ArrowRight className="w-5 h-5 text-white/70 group-hover:text-accent transition-colors duration-300" />
                    </motion.div>
                  </motion.div>
                </div>
                
                {/* Hover glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-accent2/10 to-accent/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}